/**
 * TasksView - Main view for task management (Kanban board)
 */
import React, { useState, useEffect } from 'react';
import { KanbanBoard } from './KanbanBoard';
import { TaskDialog } from './TaskDialog';
import { Task } from '../../types/task';
import { useTaskStore } from '../../stores/taskStore';
import { useProjectStore } from '../../stores/projectStore';
import './TasksView.css';

export const TasksView: React.FC = () => {
  const { currentProject } = useProjectStore();
  const { stats, fetchStats, autoGenerateTasks } = useTaskStore();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (currentProject?.id) {
      fetchStats(currentProject.id);
    }
  }, [currentProject?.id, fetchStats]);

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsDialogOpen(true);
  };

  const handleNewTask = () => {
    setSelectedTask(null);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedTask(null);
    // Refresh stats after dialog closes
    if (currentProject?.id) {
      fetchStats(currentProject.id);
    }
  };

  const handleAutoGenerate = async () => {
    if (!currentProject?.id) return;
    
    if (window.confirm('Automaticky vygenerovať úlohy z dát projektu (stĺpiky s kazetami, ONU, atď.)?')) {
      await autoGenerateTasks(currentProject.id);
      if (currentProject?.id) {
        fetchStats(currentProject.id);
      }
    }
  };

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <p className="text-gray-600 text-lg">Vyberte projekt pre zobrazenie úloh</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Úlohy - Kanban Board</h1>
            <p className="text-gray-600 mt-1">Projekt: {currentProject.name}</p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleAutoGenerate}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              ⚡ Auto-generovať úlohy
            </button>
            <button
              onClick={handleNewTask}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              ➕ Nová úloha
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-4 grid grid-cols-5 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">Celkom</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600">TODO</div>
              <div className="text-2xl font-bold text-gray-700">{stats.todo}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600">PREBIEHA</div>
              <div className="text-2xl font-bold text-blue-700">{stats.in_progress}</div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="text-sm text-yellow-600">TESTING</div>
              <div className="text-2xl font-bold text-yellow-700">{stats.testing}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600">DONE</div>
              <div className="text-2xl font-bold text-green-700">{stats.done}</div>
            </div>
          </div>
        )}

        {/* Completion rate */}
        {stats && stats.total > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Dokončené</span>
              <span className="text-sm font-medium text-gray-900">
                {stats.completion_rate.toFixed(1)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${stats.completion_rate}%` }}
              />
            </div>
          </div>
        )}

        {/* Remaining tasks summary */}
        {stats && stats.total > 0 && (
          <div className="mt-4 text-sm text-gray-600">
            <span className="font-medium">Zostáva spraviť:</span>{' '}
            {stats.todo + stats.in_progress + stats.testing} úloh
            {stats.todo > 0 && ` (${stats.todo} nových)`}
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-hidden p-6">
        <KanbanBoard onTaskClick={handleTaskClick} />
      </div>

      {/* Task Dialog */}
      <TaskDialog
        task={selectedTask}
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
      />
    </div>
  );
};

